import { NativeSelect } from "@mantine/core";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { apiClient, apiErrorMessage, callFunction } from "../api/client";
import type { Campaign, CampaignCompare, CampaignCompareRow, Job } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { campaignPath } from "../lib/campaign";
import { dateAndTime, orAbsent, shortDate } from "../lib/format";
import { FORMAT_WORDS } from "../lib/importRead";
import { PAGE_SIZE, useListParams } from "../lib/useListParams";
import type { Filters } from "../lib/campaignFilters";
import { CriteriaGroups } from "../components/CampaignCriteria";
import { Reasons, reasonsOf } from "../components/Reasons";
import { RecordLink } from "../components/RecordLink";
import { Spinner } from "../components/Spinner";
import { Badge, type Tone } from "../ui/Badge";
import { Button } from "../ui/Button";
import { DataTable, type ColumnSpec } from "../ui/DataTable";
import { FiltersPanel, Toolbar } from "../ui/Toolbar";

/* Compare an import with a campaign: which of the file's properties are on
 * the campaign's list, and why each of the others is not.
 *
 * James, 2026-10-23: a summary at the top that lists the campaign's
 * criteria, then a breakdown of the properties that do not match and why,
 * each reason short, "Type: Mixed use". Nestor had asked on the call that day
 * for a file to be checked against a campaign's criteria when it is loaded.
 * docs/plans/campaign-compare-and-changes.md in the engine.
 *
 * The engine does the comparing (ingestion/campaign_compare.py, migration
 * 288): choosing a campaign queues a job, which ranks the whole book under
 * the campaign's criteria as an update of the list does, and this reads its
 * answer from api.campaign_compare and api.campaign_compare_row. The page
 * adds no rule and no wording of its own to a reason. The campaign and the
 * comparison are in the URL, so a link opens the same answer. */

type Totals = { on_list?: number; joins?: number; leaves?: number; off?: number };
type Tally = { label: string; count: number }[];
type People = { total?: number; to_call?: number };
type Companies = { total?: number; owners?: number };

const ANSWER: Record<string, { label: string; tone: Tone }> = {
  off:     { label: "Not on the list", tone: "quiet" },
  leaves:  { label: "Leaves at the next update", tone: "refusal" },
  joins:   { label: "Joins at the next update", tone: "waiting" },
  on_list: { label: "On the list", tone: "ok" },
};

function fileOf(job: Job | null | undefined): string {
  const p = (job?.params ?? {}) as Record<string, unknown>;
  const key = typeof p["key"] === "string" ? p["key"] : "";
  return key.split("/").pop() || "a file";
}

function formatOf(job: Job | null | undefined): string {
  const p = (job?.params ?? {}) as Record<string, unknown>;
  const f = typeof p["format"] === "string" ? p["format"] : "";
  return FORMAT_WORDS[f] ?? f;
}

const n = (v: number | undefined) => (v ?? 0).toLocaleString();
const properties = (v: number) => (v === 1 ? "property" : "properties");

/** The summary's first sentences, from the engine's totals. */
function verdict(t: Totals): string[] {
  const on = t.on_list ?? 0, joins = t.joins ?? 0, leaves = t.leaves ?? 0, off = t.off ?? 0;
  const total = on + joins + leaves + off;
  if (!total) return [];
  const out = [`This file has ${n(total)} ${properties(total)}. `
    + (off + joins === 0 ? `All ${total === 1 ? "of it is" : "are"} on the list.`
      : on + leaves === 0 ? `None ${total === 1 ? "is" : "are"} on the list.`
      : `${n(on + leaves)} ${on + leaves === 1 ? "is" : "are"} on the list and ${n(off + joins)} ${off + joins === 1 ? "is" : "are"} not.`)];
  if (joins) out.push(`${n(joins)} of those not on it ${joins === 1 ? "matches" : "match"} the criteria and ${joins === 1 ? "joins" : "join"} the list at its next update.`);
  if (leaves) out.push(`${n(leaves)} on the list no longer ${leaves === 1 ? "matches" : "match"} and ${leaves === 1 ? "leaves" : "leave"} at its next update.`);
  return out;
}

export function ImportCompare({ session }: { session: SignedIn }) {
  const { id = "" } = useParams();
  const token = session.idToken;
  const [search, setSearch] = useSearchParams();
  const campaign = search.get("campaign") ?? "";
  const run = search.get("run") ?? "";
  const params = useListParams("seq", ["answer", "reason"], true, "campaign_compare_row", "c.");
  const [filtersOpen, setFiltersOpen] = useState(Object.keys(params.filters).length > 0);

  // replace for a comparison a link asked for, so Back does not return to
  // the link and ask again.
  const setRun = (name: string, jobId: string, replace = false) => setSearch((p) => {
    const next = new URLSearchParams(p);
    if (name) next.set("campaign", name); else next.delete("campaign");
    if (jobId) next.set("run", jobId); else next.delete("run");
    for (const k of [...next.keys()]) if (k.startsWith("c.")) next.delete(k);
    return next;
  }, { replace });

  const job = useQuery({
    queryKey: ["import", id],
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("job")
        .select("id,kind,status,params,requested_by,result,error,queued_at,finished_at")
        .eq("id", id).eq("kind", "file_import").limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? []) as Job[])[0] ?? null;
    },
  });

  const campaigns = useQuery({
    queryKey: ["campaign-choices"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("campaign")
        .select("name,label").is("archived_at", null).order("label").limit(500);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as Pick<Campaign, "name" | "label">[];
    },
  });

  const chosen = useQuery({
    queryKey: ["campaign", campaign, "compare"],
    enabled: Boolean(campaign),
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("campaign")
        .select("name,label,filters,list_at").eq("name", campaign).limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? []) as Pick<Campaign, "name" | "label" | "filters" | "list_at">[])[0] ?? null;
    },
  });

  const compare = useMutation({
    mutationFn: async ({ name }: { name: string; replace?: boolean }) =>
      callFunction<string>(token, "compare_with_campaign", { p_import: id, p_campaign: name }),
    onSuccess: (jobId, v) => setRun(v.name, jobId, Boolean(v.replace)),
  });
  // The campaign on screen: the one being asked about while a comparison
  // starts, else the one in the URL.
  const showing = compare.isPending ? compare.variables?.name ?? campaign : campaign;
  const labelOf = (name: string) =>
    (campaigns.data ?? []).find((c) => c.name === name)?.label
    ?? (chosen.data?.name === name ? chosen.data?.label : null) ?? name;

  // A link with a campaign and no comparison asks for one, once.
  const asked = useRef(false);
  useEffect(() => {
    if (campaign && !run && !asked.current) {
      asked.current = true;
      compare.mutate({ name: campaign, replace: true });
    }
  }, [campaign, run, compare]);

  const result = useQuery({
    queryKey: ["campaign-compare", run],
    enabled: Boolean(run),
    // Until the job is done, asked again every two seconds.
    refetchInterval: (q) => {
      const s = (q.state.data as CampaignCompare | null | undefined)?.status;
      return s === "queued" || s === "running" ? 2000 : false;
    },
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("campaign_compare")
        .select("job_id,campaign,status,error,finished_at,asked_by,list_at,ran_at,totals,reasons,people,companies,criteria")
        .eq("job_id", run).limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? []) as CampaignCompare[])[0] ?? null;
    },
  });
  const done = result.data?.status === "done";
  const answer = params.filters["answer"] ?? "";
  const reason = params.filters["reason"] ?? "";

  const rows = useQuery({
    queryKey: ["campaign-compare-rows", run, params.page, params.sorts, params.filters, params.search],
    enabled: done,
    // The rows stay while a filter, a sort or a page loads.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const from = (params.page - 1) * PAGE_SIZE;
      let q = apiClient(token).from("campaign_compare_row")
        .select("job_id,seq,property_id,label,city,state,status,rank,reasons", { count: "exact" })
        .eq("job_id", run);
      if (answer) q = q.eq("status", answer);
      // As a JSON string: given an array, the client writes a Postgres array
      // literal, which a jsonb column refuses.
      if (reason) q = q.contains("reasons", JSON.stringify([{ label: reason }]));
      if (params.search.trim()) q = q.ilike("label", `*${params.search.trim()}*`);
      for (const s of params.sorts) q = q.order(s.column, { ascending: s.ascending, nullsFirst: false });
      const { data, error, count } = await q.order("seq", { ascending: true }).range(from, from + PAGE_SIZE - 1);
      if (error) throw new Error(apiErrorMessage(error));
      return { rows: (data ?? []) as CampaignCompareRow[], total: count ?? null };
    },
  });

  const totals = (result.data?.totals ?? {}) as Totals;
  const tally = (Array.isArray(result.data?.reasons) ? result.data?.reasons : []) as Tally;
  const people = (result.data?.people ?? {}) as People;
  const companies = (result.data?.companies ?? {}) as Companies;
  const label = labelOf(showing);
  // The criteria the comparison ran under, kept on its result, else the
  // campaign's own for a comparison made before they were kept.
  const ranUnder = (result.data?.criteria ?? chosen.data?.filters ?? null) as Filters | null;
  const hasCriteria = Boolean(ranUnder && Object.values(ranUnder).some((v) =>
    v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0)));
  const filterReason = (r: string) =>
    params.set({ filters: { ...params.filters, reason: params.filters["reason"] === r ? "" : r }, page: 1 });

  const columns: ColumnSpec<CampaignCompareRow>[] = [
    { id: "property", header: "Property", role: "leading", fields: [], title: true, narrowLabel: null, sort: "label",
      cell: (r) => <RecordLink to="properties" id={r.property_id} label={r.label} /> },
    { id: "city", header: "City", role: "secondary", fields: [], narrowLabel: null, sort: "city",
      cell: (r) => orAbsent(r.city) },
    { id: "state", header: "State", role: "secondary", fields: [], narrowLabel: null, sort: "state",
      cell: (r) => orAbsent(r.state) },
    { id: "answer", header: "On the list", role: "leading", fields: [], narrowLabel: null, sort: "seq",
      cell: (r) => {
        const a = ANSWER[r.status ?? ""];
        return a ? <Badge tone={a.tone}>{a.label}</Badge> : orAbsent(r.status);
      } },
    { id: "why", header: "Why not", role: "leading", fields: [], narrowLabel: null,
      cell: (r) => (reasonsOf(r.reasons).length ? <Reasons reasons={reasonsOf(r.reasons)} /> : null) },
    { id: "rank", header: "Rank", role: "secondary", fields: [], align: "num", narrowLabel: "rank", sort: "rank",
      cell: (r) => (r.rank ? r.rank.toLocaleString() : null) },
  ];

  const chips = [
    ...(answer ? [{ label: `On the list: ${ANSWER[answer]?.label ?? answer}`,
                    onRemove: () => params.set({ filters: { ...params.filters, answer: "" }, page: 1 }) }] : []),
    ...(reason ? [{ label: `Why not: ${reason}`,
                    onRemove: () => params.set({ filters: { ...params.filters, reason: "" }, page: 1 }) }] : []),
  ];
  const total = rows.data?.total ?? null;
  const lastPage = total === null ? params.page : Math.max(1, Math.ceil(total / PAGE_SIZE));
  const sum = (totals.on_list ?? 0) + (totals.joins ?? 0) + (totals.leaves ?? 0) + (totals.off ?? 0);

  return (
    <main className="cmp">
      <p><Link to="/import">Back to Import</Link></p>
      <h1>Compare with a campaign</h1>
      {job.isLoading ? <p className="muted"><Spinner />Loading the import.</p>
        : job.isError ? <p className="refusal" role="alert">{(job.error as Error).message}</p>
        : !job.data ? <p className="refusal" role="alert">No import has this address. <Link to="/import">Back to Import</Link></p>
        : (
          <p>
            <strong>{fileOf(job.data)}</strong>
            <span className="muted"> · {formatOf(job.data)} · loaded {shortDate(job.data.finished_at ?? job.data.queued_at)}</span>
            {" · "}<Link to={`/imports/${id}`}>Records</Link>
          </p>
        )}

      {job.data ? (
        <div className="cmp-pick">
          <div className="cmp-pick-select">
          <NativeSelect
            label="Campaign"
            value={showing}
            disabled={compare.isPending}
            data={[{ value: "", label: campaigns.isLoading ? "Loading the campaigns" : "Choose a campaign" },
                   ...(campaigns.data ?? []).map((c) => ({ value: c.name ?? "", label: c.label ?? c.name ?? "" })),
                   // A campaign in the link that is no longer open still shows.
                   ...(showing && campaigns.data && !campaigns.data.some((c) => c.name === showing)
                     ? [{ value: showing, label: `${labelOf(showing)} (not open)` }] : [])]}
            onChange={(e) => {
              const name = e.currentTarget.value;
              if (name) compare.mutate({ name });
              else setRun("", "");
            }}
          />
          </div>
          {campaign && !compare.isPending && result.data?.status !== "queued" && result.data?.status !== "running" ? (
            <Button onClick={() => compare.mutate({ name: campaign })}>{run && result.data ? "Compare again" : "Compare"}</Button>
          ) : null}
        </div>
      ) : null}
      {campaigns.isError ? <p className="refusal" role="alert">The campaigns did not load. {(campaigns.error as Error).message}</p> : null}
      {compare.isError ? <p className="refusal" role="alert">{(compare.error as Error).message}</p> : null}

      {!campaign && job.data ? (
        <p className="muted">Choose a campaign to see which of this file's properties are on its list, and why the others are not.</p>
      ) : null}

      {run && result.isError ? (
        <p className="refusal" role="alert">
          {(result.error as Error).message} <Button size="compact-sm" onClick={() => result.refetch()}>Try again</Button>
        </p>
      ) : null}
      {compare.isPending || (run && (result.isLoading || result.data?.status === "queued" || result.data?.status === "running")) ? (
        <p role="status"><Spinner />Comparing with {label}.</p>
      ) : null}
      {result.data?.status === "failed" ? (
        <p className="refusal" role="alert">The comparison did not finish. {result.data.error || "No reason was recorded."}</p>
      ) : null}
      {result.data?.status === "cancelled" ? <p className="muted" role="status">The comparison was cancelled. Compare to ask again.</p> : null}
      {run && result.data === null ? <p className="refusal" role="alert">No comparison has this address. Compare to ask again.</p> : null}

      {done && result.data ? (
        <>
          <section className="cmp-summary" aria-labelledby="cmp-campaign">
            <h2 id="cmp-campaign"><Link to={campaignPath(campaign)}>{label}</Link></h2>
            {sum ? verdict(totals).map((s) => <p key={s} className="cmp-verdict">{s}</p>) : (
              <p>This file touched no properties. A campaign lists properties, so the file's people and companies reach a list only through one.</p>
            )}
            {totals.joins ? (
              <p className="muted">Update the list on <Link to={campaignPath(campaign)}>the campaign</Link> to add them.</p>
            ) : null}
            {tally.length ? (
              <div className="cmp-tally">
                <span className="cmp-tally-h">What keeps them off</span>
                {tally.map((t) => (
                  <button type="button" key={t.label} className={`chip cmp-reason${reason === t.label ? " on" : ""}`}
                          aria-pressed={reason === t.label} onClick={() => filterReason(t.label)}>
                    {t.label} <strong>{t.count.toLocaleString()}</strong>
                  </button>
                ))}
              </div>
            ) : null}
            {(people.total ?? 0) > 0 || (companies.total ?? 0) > 0 ? (
              <ul className="cmp-also">
                {(people.total ?? 0) > 0 ? (
                  <li>People: {n(people.to_call)} of the file's {n(people.total)} {people.to_call === 1 ? "is" : "are"} among the people to call on this list.</li>
                ) : null}
                {(companies.total ?? 0) > 0 ? (
                  <li>Companies: {n(companies.owners)} of the file's {n(companies.total)} {companies.owners === 1 ? "owns" : "own"} a property on the list.</li>
                ) : null}
              </ul>
            ) : null}
            <p className="muted">
              Compared {dateAndTime(result.data.ran_at)}
              {result.data.list_at ? <>, with the list as of {dateAndTime(result.data.list_at)}</> : ". The campaign has no list yet, so every property that matches joins at its first update"}.
              {result.data.asked_by ? ` Asked by ${result.data.asked_by}.` : ""}
            </p>
            <h3>The campaign's criteria</h3>
            {ranUnder ? (hasCriteria ? <CriteriaGroups token={token} filters={ranUnder} />
                : <p className="muted">No criteria. This campaign matches every property.</p>)
              : chosen.isError ? <p className="refusal" role="alert">The criteria did not load. {(chosen.error as Error).message}</p>
              : <p className="muted"><Spinner />Loading.</p>}
          </section>

          {sum ? (
            <section className="cmp-rows" aria-labelledby="cmp-rows-h">
              <h2 id="cmp-rows-h">
                The file's properties{" "}
                {total !== null ? <span className="muted ui-section-count">({total.toLocaleString()})</span> : null}
              </h2>
              <Toolbar
                id="compare"
                search={{ label: "Search by property", value: params.search, onSubmit: (s) => params.set({ search: s, page: 1 }) }}
                filtersCount={chips.length}
                filtersOpen={filtersOpen}
                onToggleFilters={() => setFiltersOpen((v) => !v)}
                chips={chips}
                onClearAll={() => params.set({ search: "", filters: {}, page: 1 })}
              />
              <FiltersPanel id="compare" open={filtersOpen} onDone={() => setFiltersOpen(false)}>
                <div className="filters-own">
                  <NativeSelect label="On the list" value={answer}
                                data={[{ value: "", label: "Any" },
                                       ...Object.entries(ANSWER).map(([value, a]) => ({ value, label: a.label }))]}
                                onChange={(e) => params.set({ filters: { ...params.filters, answer: e.currentTarget.value }, page: 1 })} />
                  <NativeSelect label="Why not" value={reason}
                                data={[{ value: "", label: "Any" }, ...tally.map((t) => ({ value: t.label, label: t.label }))]}
                                onChange={(e) => params.set({ filters: { ...params.filters, reason: e.currentTarget.value }, page: 1 })} />
                </div>
              </FiltersPanel>
              {rows.isLoading ? <p className="muted"><Spinner />Loading.</p>
                : rows.isError ? (
                  <p className="refusal" role="alert">
                    {(rows.error as Error).message} <Button size="compact-sm" onClick={() => rows.refetch()}>Try again</Button>
                  </p>
                ) : !(rows.data?.rows ?? []).length ? (
                  <p className="muted">
                    No property matches. <Button kind="link" size="compact-sm" onClick={() => params.set({ search: "", filters: {}, page: 1 })}>Clear</Button>
                  </p>
                ) : (
                  <DataTable columns={columns} rows={rows.data?.rows ?? []} params={params}
                             rowKey={(r) => `${r.property_id}-${r.seq}`} />
                )}
              {lastPage > 1 ? (
                <div className="bar">
                  <Button onClick={() => params.set({ page: params.page - 1 })} disabled={params.page <= 1}>Previous</Button>
                  <span className="muted">Page {params.page.toLocaleString()} of {lastPage.toLocaleString()}</span>
                  <Button onClick={() => params.set({ page: params.page + 1 })} disabled={params.page >= lastPage}>Next</Button>
                </div>
              ) : null}
            </section>
          ) : null}
        </>
      ) : null}
    </main>
  );
}

